/**
 * Time logged against a request, to the minute.
 *
 * Hours are the unit of record everywhere else in the product — the column, the estimate, the
 * invoice line — so this converts rather than replaces. A minute is 1/60 of an hour and no
 * decimal expansion of it terminates, so every value that reaches the database goes through
 * `quantizeToMinutes` first, which rounds it to a whole minute at the column's scale.
 *
 * NOT FOR THE ROUND TRIP — that survives either way. Rounding `m/60` to a hundredth moves it by
 * at most 0.3 of a minute, so `hoursToMinutes` recovers the minute somebody typed even at the
 * two decimals the column used to hold. What the scale buys is the SUM: `completedSummary` adds
 * the stored column up in Postgres, and two hundred requests of 1 h 20 m each sum to 266.00 h at
 * two decimals against a true 266.67 — forty minutes missing from a billing total. Four decimals
 * make the same two hundred rows drift by 24 seconds. See the 2026-09-10 entry in
 * `migrations/README.md`, whose migration header gets this reason wrong and cannot be edited.
 */
export const MINUTES_PER_HOUR = 60;
/** Decimal places `issue.actual_hours` holds. Migration 0029 widened it from 2 to 4. */
export const HOURS_SCALE = 4;
const SCALE = 10 ** HOURS_SCALE;
/** Whole minutes in an hours figure. The inverse of `minutesToHours`, exact for stored values. */
export function hoursToMinutes(hours) {
    if (!Number.isFinite(hours))
        return 0;
    return Math.round(hours * MINUTES_PER_HOUR);
}
/** Minutes as hours, rounded to the scale the column actually stores. */
export function minutesToHours(minutes) {
    if (!Number.isFinite(minutes))
        return 0;
    return Math.round((minutes / MINUTES_PER_HOUR) * SCALE) / SCALE;
}
/**
 * Round an hours figure to the nearest whole minute, at the column's scale.
 *
 * Non-finite input becomes zero rather than propagating: Postgres ACCEPTS `NaN` in a numeric
 * column, so a NaN that reaches this path is not rejected at the boundary — it is stored, and
 * every sum and average over that organization's billing figures is NaN from then on.
 */
export function quantizeToMinutes(hours) {
    return minutesToHours(hoursToMinutes(hours));
}
/**
 * Read a duration a person typed, in hours. Null when it is not a duration.
 *
 * A BARE NUMBER IS HOURS. The field has always been "Actual hours" and staff have always typed
 * `1.5` into it; reading that as ninety minutes would silently rewrite every habit at once, and
 * in the direction that under-bills. Minutes need a unit — `90m` — or the colon form.
 *
 * Accepted: `1.5`, `2h`, `90m`, `1h30`, `1h 30m`, `1 hr 30 min`, `1:30`.
 */
export function parseDuration(input) {
    const text = input.trim().toLowerCase();
    if (text === '')
        return null;
    const colon = /^(\d+):([0-5]\d)$/.exec(text);
    if (colon)
        return minutesToHours(Number(colon[1]) * MINUTES_PER_HOUR + Number(colon[2]));
    if (/^\d+(?:\.\d+)?$/.test(text))
        return quantizeToMinutes(Number(text));
    // The minute unit is optional so `1h30` works, but only ever after an hour part: a bare `30`
    // never reaches here, it is caught by the branch above and read as thirty hours.
    const parts = /^(?:(\d+(?:\.\d+)?)\s*(?:h|hr|hrs|hour|hours))?\s*(?:(\d+(?:\.\d+)?)\s*(?:m|min|mins|minute|minutes)?)?$/.exec(text);
    if (!parts)
        return null;
    const [, hoursPart, minutesPart] = parts;
    if (hoursPart === undefined && minutesPart === undefined)
        return null;
    const hours = hoursPart === undefined ? 0 : Number(hoursPart);
    const minutes = minutesPart === undefined ? 0 : Number(minutesPart);
    return quantizeToMinutes(hours + minutes / MINUTES_PER_HOUR);
}
//# sourceMappingURL=duration.js.map